import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAppContext } from '../lib/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
// Removed role select
import { useLocation } from 'wouter';
import { AuthToaster } from '../components/ui/auth-toaster';
import { supabase } from '../lib/supabase';

function validateEmail(email: string): string {
  if (!email.trim()) return 'Please enter your email address.';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  
  // Extract domain from email
  const domain = email.split('@')[1];
  
  // Check for obviously fake domains
  const fakeDomains = [
    'test.com', 'example.com', 'fake.com', 'dummy.com', 'invalid.com',
    'test.org', 'example.org', 'fake.org', 'dummy.org', 'invalid.org',
    'test.net', 'example.net', 'fake.net', 'dummy.net', 'invalid.net',
    'localhost', '127.0.0.1', '0.0.0.0', 'nonexistent.com', 'notreal.com'
  ];
  if (fakeDomains.includes(domain.toLowerCase())) {
    return 'Please use a real email address';
  }
  
  return '';
}

// Async function to validate domain exists
async function validateEmailDomain(email: string): Promise<string> {
  const domain = email.split('@')[1];
  
  try {
    // Use a simple DNS lookup to check if domain exists
    // This is a basic check - for production, you might want to use a more robust service
    const response = await fetch(`https://dns.google/resolve?name=${domain}&type=MX`);
    const data = await response.json();
    
    if (data.Status === 0 && data.Answer && data.Answer.length > 0) {
      return ''; // Domain has MX records, likely valid
    } else {
      return 'This email address does not exist. Please check your email address.';
    }
  } catch (error) {
    // If DNS lookup fails, don't block the user
    return '';
  }
}

function validatePassword(password: string, name: string, email: string): string {
  if (password.length < 10) return 'Password must be at least 10 characters long.';
  if (!/[A-Z]/.test(password)) return 'Password must include at least one uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Password must include at least one lowercase letter.';
  if (!/[0-9]/.test(password)) return 'Password must include at least one number.';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must include at least one special character.';
  if (/^\s|\s$/.test(password)) return 'Password cannot start or end with a space.';
  const weak = ['password', '123456', 'qwerty', 'letmein', '1111', 'abc123', 'password123', 'admin', 'welcome'];
  if (weak.some(w => password.toLowerCase().includes(w))) return 'Password is too common or easily guessed.';
  if (name && password.toLowerCase().includes(name.toLowerCase())) return 'Password cannot contain your name.';
  if (email && password.toLowerCase().includes(email.split('@')[0].toLowerCase())) return 'Password cannot contain your email.';
  if (/([a-zA-Z0-9])\1{2,}/.test(password)) return 'Password cannot contain simple repeated characters.';
  if (/0123|1234|2345|3456|4567|5678|6789|7890|abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz/i.test(password)) return 'Password cannot contain simple sequences.';
  return '';
}

function getPasswordRequirements(): string {
  return 'Password must be at least 10 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character. Password cannot start or end with a space, contain common words, your name, your email, repeated characters, or simple sequences.';
}

// Helper to parse query string
function getQueryParam(search: string, key: string): string | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  return params.get(key);
}

export default function AuthPage() {
  const { auth } = useAppContext();
  const { startGuestSession } = useAppContext();
  const { register, login, error: authError, clearError } = auth;
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPasswordPopover, setShowPasswordPopover] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [phone, setPhone] = useState('');
  // Role is fixed to 'client' for signup
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [showAccountExists, setShowAccountExists] = useState(false);
  const [showDuplicateEmailPopover, setShowDuplicateEmailPopover] = useState(false);
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [originalTopPosition, setOriginalTopPosition] = useState<string | null>(null);
  const [isEmailDomainError, setIsEmailDomainError] = useState(false);

  // Handle email confirmation redirect
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const type = urlParams.get('type');
      const token = urlParams.get('token');
      const hash = urlParams.get('hash');
      
      console.log('🔍 [AuthPage] Checking URL parameters:', { type, token, hash });
      
      // Check if this is an email confirmation
      if (type === 'email_change' && (token || hash)) {
        console.log('📧 [AuthPage] Email confirmation detected, redirecting to success page');
        setLocation('/email-change-success');
        return;
      }
      
      // Check if this is a password reset confirmation
      if (type === 'recovery' && (token || hash)) {
        console.log('🔑 [AuthPage] Password reset confirmation detected');
        // Handle password reset if needed
        return;
      }
    };
    
    handleEmailConfirmation();
  }, [setLocation]);

  // Lock welcome page in place (prevent any keyboard repositioning)
  useEffect(() => {
    if (!isRegister) {
      // Clean up any residual styles from register mode first
      const cardContainerElement = document.querySelector('.auth-card-container') as HTMLElement;
      if (cardContainerElement) {
        // Reset all positioning styles
        cardContainerElement.style.marginTop = '';
        cardContainerElement.style.transition = '';
        cardContainerElement.style.position = '';
        cardContainerElement.style.transform = '';
        cardContainerElement.style.top = '';
        cardContainerElement.style.left = '';
        cardContainerElement.style.right = '';
        cardContainerElement.style.bottom = '';
        
        // Force reset with important flags
        cardContainerElement.style.setProperty('margin-top', '144px', 'important');
        cardContainerElement.style.setProperty('transition', 'none', 'important');
        cardContainerElement.style.setProperty('animation', 'none', 'important');
        cardContainerElement.style.setProperty('transform', 'none', 'important');
      }
      
      // Store original styles
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyPosition = document.body.style.position;
      const originalBodyWidth = document.body.style.width;
      const originalBodyHeight = document.body.style.height;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      // Prevent all scrolling and viewport changes for login page
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent iOS Safari from adjusting viewport
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
      
      // Store the initial viewport height
      const initialViewportHeight = window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${initialViewportHeight * 0.01}px`);
      
      // Prevent viewport changes
      const preventKeyboardResize = () => {
        const currentHeight = window.innerHeight;
        document.documentElement.style.setProperty('--vh', `${currentHeight * 0.01}px`);
      };
      
      window.addEventListener('resize', preventKeyboardResize);
      window.addEventListener('orientationchange', preventKeyboardResize);
      
      return () => {
        // Remove event listeners
        window.removeEventListener('resize', preventKeyboardResize);
        window.removeEventListener('orientationchange', preventKeyboardResize);
        
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.position = originalBodyPosition;
        document.body.style.width = originalBodyWidth;
        document.body.style.height = originalBodyHeight;
        document.body.style.top = '';
        document.body.style.left = '';
        document.documentElement.style.overflow = originalHtmlOverflow;
        
        // Restore viewport
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
        }
      };
    }
  }, [isRegister]);

  // Handle keyboard show/hide and card positioning (only for register mode)
  useEffect(() => {
    // Only apply keyboard positioning logic for register mode
    if (!isRegister) {
      return;
    }

    // Store original styles
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyWidth = document.body.style.width;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    
    // Prevent all scrolling and viewport changes
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.documentElement.style.overflow = 'hidden';
    
    // Prevent iOS Safari from adjusting viewport
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
    }
    
    // Store the initial viewport height
    const initialViewportHeight = window.innerHeight;
    document.documentElement.style.setProperty('--vh', `${initialViewportHeight * 0.01}px`);
    
    // Function to restore card to original position
    const restoreCardPosition = () => {
      const cardContainerElement = document.querySelector('.auth-card-container') as HTMLElement;
      if (cardContainerElement) {
        const correctOriginalPosition = '280px'; // Only register mode uses this
        cardContainerElement.style.marginTop = correctOriginalPosition;
        cardContainerElement.style.transition = 'none';
      }
    };
    
    // Function to handle keyboard show
    const handleKeyboardShow = () => {
      // Let iOS handle the positioning naturally when keyboard shows
      const cardContainerElement = document.querySelector('.auth-card-container') as HTMLElement;
      if (cardContainerElement) {
        cardContainerElement.style.transition = 'none';
      }
    };
    
    // Function to handle keyboard hide
    const handleKeyboardHide = () => {
      // Restore original position when keyboard hides
      setTimeout(() => {
        restoreCardPosition();
      }, 100); // Small delay to ensure keyboard is fully hidden
    };
    
    // Track keyboard state
    let keyboardVisible = false;
    let lastHeight = initialViewportHeight;
    
    // Handle viewport changes
    const handleViewportChange = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = Math.abs(currentHeight - lastHeight);
      
      // If height changed significantly, keyboard state likely changed
      if (heightDifference > 150) {
        if (currentHeight < lastHeight) {
          // Height decreased - keyboard likely shown
          if (!keyboardVisible) {
            keyboardVisible = true;
            handleKeyboardShow();
          }
        } else {
          // Height increased - keyboard likely hidden
          if (keyboardVisible) {
            keyboardVisible = false;
            handleKeyboardHide();
          }
        }
        lastHeight = currentHeight;
      }
    };
    
    // Listen for viewport changes
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    
    // Handle input focus/blur events for better keyboard detection
    const inputs = document.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        // Small delay to let keyboard appear
        setTimeout(() => {
          if (!keyboardVisible) {
            keyboardVisible = true;
            handleKeyboardShow();
          }
        }, 300);
      });
      
      input.addEventListener('blur', () => {
        // Small delay to let keyboard hide
        setTimeout(() => {
          if (keyboardVisible) {
            keyboardVisible = false;
            handleKeyboardHide();
          }
        }, 300);
      });
    });
    
    // Set initial card position
    restoreCardPosition();
    
    return () => {
      // Remove event listeners
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      
      // Restore original styles
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.width = originalBodyWidth;
      document.body.style.height = originalBodyHeight;
      document.body.style.top = '';
      document.body.style.left = '';
      document.documentElement.style.overflow = originalHtmlOverflow;
      
      // Restore viewport
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
      }
    };
  }, [isRegister]);

  // Show login/register form based on mode in query string
  useEffect(() => {
    const search = location.split('?')[1] || '';
    let mode = getQueryParam(search, 'mode');
    if (!mode && typeof window !== 'undefined') {
      const qs = window.location.search;
      mode = getQueryParam(qs, 'mode') || undefined as any;
      if (!mode) {
        try {
          const stored = sessionStorage.getItem('auth_mode');
          if (stored === 'login' || stored === 'register') {
            mode = stored as any;
            sessionStorage.removeItem('auth_mode');
          }
        } catch {}
      }
    }
    if (mode === 'login') {
      setIsRegister(false);
    } else if (mode === 'register') {
      setIsRegister(true);
    }
  }, [location]);


  // Clear error if user becomes authenticated
  useEffect(() => {
    if (clearError) clearError();
    // eslint-disable-next-line
  }, []);

  // Automatically clear error after 5 seconds, then fade out
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
        setTimeout(() => setError(''), 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fade out Account Exists message after 5 seconds
  useEffect(() => {
    if (showAccountExists) {
      const timer = setTimeout(() => {
        setShowAccountExists(false);
      }, 5000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [showAccountExists]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Mark that user has attempted to submit
    setHasAttemptedSubmit(true);

    // Clear all previous errors first
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setNameError('');
    setPhoneError('');
    setError('');
    setShowError(false);
    setShowPasswordPopover(false);
    setIsEmailDomainError(false);

    // Check all fields and collect all validation errors
    let hasErrors = false;
    const errors: string[] = [];

    // Email validation
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setIsEmailDomainError(true);
      hasErrors = true;
      errors.push('Email: ' + emailValidationError);
    } else {
      // If basic validation passes, check domain validity
      try {
        const domainError = await validateEmailDomain(email);
        if (domainError) {
          setEmailError(domainError);
          setIsEmailDomainError(true);
          hasErrors = true;
          errors.push('Email: ' + domainError);
        }
      } catch (error) {
        // Don't block registration if domain validation fails
      }
    }
    
    // Password validation
    if (!password.trim()) {
      setPasswordError("Please enter a password.");
      hasErrors = true;
      errors.push('Please enter a password.');
    }

    // Registration-specific validations
    if (isRegister) {
      if (!name.trim()) {
        setNameError("Please enter your name.");
        hasErrors = true;
        errors.push('Name is required.');
      }

      // Phone number is now optional - no validation needed

      // Password confirmation validation
      if (!confirmPassword.trim()) {
        setConfirmPasswordError('Please confirm your password.');
        hasErrors = true;
        errors.push('Please confirm your password.');
      } else if (password !== confirmPassword) {
        setConfirmPasswordError('Passwords do not match.');
        hasErrors = true;
        errors.push('Passwords do not match.');
      }
    }

    // If there are validation errors, show them and stop
    if (hasErrors) {
      setError(errors.join('. '));
      setShowError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      if (isRegister) {
        // Validate password
        const pwdError = validatePassword(password, name, email);
        if (pwdError) {
          // Check if password is too weak (doesn't meet basic requirements)
          const isWeakPassword = password.length < 10 || 
                                !/[A-Z]/.test(password) || 
                                !/[a-z]/.test(password) || 
                                !/[0-9]/.test(password) || 
                                !/[^A-Za-z0-9]/.test(password);
          
          if (isWeakPassword) {
            setShowPasswordPopover(true);
          } else {
            setPasswordError(pwdError);
          }
          setIsSubmitting(false);
          return;
        }

        // Attempt registration
        const result = await register(name, email, password, 'client');
        
        if (!result) {
          setError('Registration failed. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Registration successful
        if (clearError) clearError();
        toast({ 
          title: 'Welcome!', 
          description: 'Your account has been created successfully.',
          variant: 'default'
        });
        setRedirecting(true);
        setLocation('/', { replace: true });
      } else {
        // Login flow
        const result = await login(email, password);
        
        if (!result) {
          setError('Login failed. Please check your credentials.');
          setIsSubmitting(false);
          return;
        }

        if (clearError) clearError();
        toast({ 
          title: 'Welcome back!', 
          description: 'You have been logged in successfully.',
          variant: 'default'
        });
        setRedirecting(true);
        setLocation('/', { replace: true });
      }
    } catch (err: any) {
      let friendlyMessage = 'An unexpected error occurred.';

      // Handle Zod-like error array
      if (Array.isArray(err) && err[0]?.message) {
        friendlyMessage = err[0].message;
      }
      // Handle object with error array (e.g., { error: [{ message, code, ... }] })
      else if (err?.error && Array.isArray(err.error) && err.error[0]?.message) {
        friendlyMessage = err.error[0].message;
      }
      // Handle stringified JSON error
      else if (typeof err === 'string') {
        try {
          const parsed = JSON.parse(err);
          if (Array.isArray(parsed) && parsed[0]?.message) {
            friendlyMessage = parsed[0].message;
          } else if (parsed?.message) {
            friendlyMessage = parsed.message;
          } else {
            friendlyMessage = err;
          }
        } catch {
          friendlyMessage = err;
        }
      }
      // Handle object with message property
      else if (err?.message) {
        friendlyMessage = err.message;
      }

      if (isRegister) {
        // Handle registration-specific errors
        if (
          friendlyMessage.includes('Password must be') ||
          friendlyMessage.includes('Password cannot') ||
          friendlyMessage.includes('Password is too')
        ) {
          setPasswordError(friendlyMessage);
        } else if (friendlyMessage.includes('already registered') || 
                   friendlyMessage.includes('exists') || 
                   friendlyMessage.includes('already in use') ||
                   friendlyMessage.includes('Email is already in use') ||
                   friendlyMessage.includes('email address is already registered')) {
          // Show popover for duplicate email and highlight email field
          setShowDuplicateEmailPopover(true);
          setIsEmailDomainError(true);
          setIsSubmitting(false);
          return;
        } else {
          setError(friendlyMessage);
          toast({ 
            title: 'Registration Failed', 
            description: friendlyMessage,
            variant: 'destructive'
          });
        }
      } else {
        setError(friendlyMessage);
        toast({ 
          title: 'Login Failed', 
          description: friendlyMessage,
          variant: 'destructive'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    try {
      // First check if the email exists in our database
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('email')
        .eq('email', email)
        .single();

      if (userError || !user) {
        setError('No account found with this email address. Please check your email or sign up for a new account.');
        return;
      }

      // New flow: send Supabase Email OTP, then navigate to reset-otp screen
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: 'https://myagentapp.grahampaigerealty.com/reset-otp'
        }
      });

      if (error) throw error;

      toast({ title: 'Check your email', description: 'Enter the 6-digit code we sent to your inbox.' });
      setLocation(`/reset-otp?email=${encodeURIComponent(email)}`);
    } catch (error: any) {
      setError(error.message || "Failed to send reset email. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        @supports (-webkit-touch-callout: none) {
          /* iOS Safari specific styles */
          .auth-container {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            height: 100vh !important;
            width: 100vw !important;
            overflow: hidden !important;
            transform: translateZ(0) !important;
            -webkit-transform: translateZ(0) !important;
          }
          
          /* Prevent iOS keyboard from affecting layout */
          html, body {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            height: 100vh !important;
            width: 100vw !important;
            overflow: hidden !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          /* Prevent input focus from moving viewport */
          input, textarea {
            -webkit-user-select: text !important;
            -webkit-appearance: none !important;
          }
        }
        
        /* General styles for all browsers */
        .auth-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          height: calc(var(--vh, 1vh) * 100);
          width: 100vw;
          overflow: hidden;
        }
      `}</style>
      <div 
        className="auth-container min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex justify-center items-center p-4 pt-safe pb-safe" 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          height: '100vh',
          width: '100vw',
          zIndex: 1
        }}
      >
      <div className={`auth-card-container flex flex-col items-center w-full max-w-md space-y-4 mx-auto ${isRegister ? 'mt-[280px]' : 'mt-36'}`}>
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
          <CardHeader className="space-y-1 border-0 ring-0 outline-none">
            <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent border-0 ring-0 outline-none">
              {isRegister ? 'Create Account' : 'Welcome'}
            </CardTitle>
            <p className="text-center text-sm text-gray-500 border-0 ring-0 outline-none">
              {isRegister ? 'Enter your details to create your account' : 'Sign in to your account'}
            </p>
          </CardHeader>
          <CardContent className="border-0 ring-0 outline-none">
            <form onSubmit={handleSubmit} className="border-0 ring-0 outline-none">
              {isRegister && (
                <>
                  <div className="space-y-2 border-0 ring-0 outline-none mb-4">
                    <label htmlFor="name" className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">
                      Name
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (showError) setShowError(false);
                        if (nameError) setNameError('');
                      }}
                      onFocus={() => {
                        if (nameError) setNameError('');
                      }}
                      disabled={isSubmitting}
                      className={`transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600 ${nameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-200'}`}
                      placeholder={nameError || ""}
                    />
                  </div>
                  <div className="space-y-2 border-0 ring-0 outline-none mb-4">
                    <label htmlFor="phone" className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">
                      Phone Number (Optional)
                    </label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (phoneError) setPhoneError('');
                      }}
                      onFocus={() => {
                        if (phoneError) setPhoneError('');
                      }}
                      disabled={isSubmitting}
                      className={`transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600 ${phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-200'}`}
                      placeholder={phoneError || "(123) 456-7890"}
                    />
                  </div>
                  
                </>
              )}
              <div className="space-y-2 border-0 ring-0 outline-none mb-4">
                <label htmlFor="email" className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">
                  Email
                </label>
                <div className="relative">
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (showError) setShowError(false);
                      if (emailError) setEmailError('');
                      if (isEmailDomainError) setIsEmailDomainError(false);
                    }}
                    onFocus={() => {
                      if (emailError) setEmailError('');
                    }}
                    disabled={isSubmitting}
                    className={`transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600 ${emailError || isEmailDomainError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-200'} ${isEmailDomainError ? 'bg-red-50' : ''}`}
                    placeholder={emailError || ""}
                  />
                </div>
              </div>
              <div className="space-y-2 border-0 ring-0 outline-none mb-4">
                <label htmlFor="password" className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">
                  Password
                </label>
                <div className="relative border-0 ring-0 outline-none">
                  <Popover open={showPasswordPopover && hasAttemptedSubmit} onOpenChange={(open) => {
                    // Only allow closing the popover, not opening it manually
                    if (!open) {
                      setShowPasswordPopover(false);
                    }
                  }}>
                    <PopoverTrigger asChild>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (showError) setShowError(false);
                            if (passwordError) setPasswordError('');
                            if (confirmPasswordError) setConfirmPasswordError('');
                            if (showPasswordPopover) setShowPasswordPopover(false);
                            // Reset submission attempt flag when user starts typing
                            if (hasAttemptedSubmit) setHasAttemptedSubmit(false);
                          }}
                          onFocus={() => {
                            if (passwordError) setPasswordError('');
                          }}
                          disabled={isSubmitting}
                          autoComplete="off"
                          className={`transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600 ${passwordError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-200'}`}
                          placeholder={passwordError || ""}
                        />
                        {showPasswordPopover && hasAttemptedSubmit && (
                          <AlertCircle className="absolute right-10 top-1/2 -translate-y-1/2 text-red-500" size={20} />
                        )}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-[324px] p-4" side="top" align="start">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Password Requirements</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>• At least 10 characters long</p>
                          <p>• One uppercase letter (A-Z)</p>
                          <p>• One lowercase letter (a-z)</p>
                          <p>• One number (0-9)</p>
                          <p>• One special character (!@#$%^&*)</p>
                          <p>• Cannot start or end with a space</p>
                          <p>• Cannot contain common words</p>
                          <p>• Cannot contain your name or email</p>
                          <p>• Cannot contain repeated characters</p>
                          <p>• Cannot contain simple sequences</p>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {!isRegister && (
              <div className="flex justify-end items-center gap-3 border-0 ring-0 outline-none">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0 [&>span]:border-0 [&>span]:ring-0 [&>span]:outline-none [&>div]:border-0 [&>div]:ring-0 [&>div]:outline-none"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>
              {isRegister && (
                <div className="space-y-2 border-0 ring-0 outline-none mb-4">
                  <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">
                    Confirm Password
                  </label>
                  <div className="relative border-0 ring-0 outline-none">
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (confirmPasswordError) setConfirmPasswordError('');
                      }}
                      onFocus={() => {
                        if (confirmPasswordError) setConfirmPasswordError('');
                      }}
                      disabled={isSubmitting}
                      autoComplete="off"
                      className={`transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600 ${confirmPasswordError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-200'}`}
                      placeholder={confirmPasswordError || ""}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <div className="mt-1 min-h-[1.25rem]" style={{ minHeight: '1.25rem' }}>
                    {confirmPasswordError && (
                      <p className="text-sm text-red-500 mt-1">{confirmPasswordError}</p>
                    )}
                  </div>
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white transition-all duration-200 transform hover:scale-[1.02] rounded-full border-0 shadow-sm ring-0 outline-none focus:ring-2 focus:ring-blue-600/40"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsRegister(!isRegister);
                  setPasswordError('');
                  setConfirmPassword('');
                  setPhone('');
                  setHasAttemptedSubmit(false);
                  setNameError('');
                  setPhoneError('');
                  setEmailError('');
                  setConfirmPasswordError('');
                  setIsEmailDomainError(false);
                  
                }}
                className="w-full text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-200 rounded-full border-0 ring-0 outline-none focus:ring-2 focus:ring-blue-600/40 mt-6"
                disabled={isSubmitting}
              >
                {isRegister ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Duplicate Email Popover */}
        {showDuplicateEmailPopover && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Email Already Registered</h3>
              </div>
              <p className="text-gray-600 mb-6">
                This email address is already registered. Would you like to sign in instead?
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDuplicateEmailPopover(false);
                    setIsRegister(false);
                    setError('');
                    setEmailError('');
                    setIsEmailDomainError(false);
                  }}
                  className="flex-1"
                >
                  Sign In
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDuplicateEmailPopover(false);
                    setIsEmailDomainError(false);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            startGuestSession();
            setLocation('/', { replace: true });
          }}
          className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200 rounded-full"
        >
          Skip for now
        </Button>
        {/* Error message below the card, always reserving space */}
        <div className="w-full max-w-md" style={{ minHeight: '3.5rem' }}>
          <div
            className={`p-4 bg-red-50 rounded-lg flex items-center gap-2 transition-opacity duration-500 ${!isRegister && error && showError ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            aria-live="polite"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-red-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
              />
            </svg>
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        </div>
        {/* Green message below the card, always reserving space */}
        <div className="w-full max-w-md" style={{ minHeight: '3.5rem' }}>
          <div
            className={`p-4 bg-green-50 rounded-lg flex items-center gap-2 transition-opacity duration-500 ${isRegister && showAccountExists ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            aria-live="polite"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-green-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-green-600 font-medium">This email is already registered. Would you like to login instead?</p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsRegister(false);
                  setPasswordError('');
                  setConfirmPassword('');
                  setShowAccountExists(false);
                  setHasAttemptedSubmit(false);
                  setNameError('');
                  setPhoneError('');
                  setEmailError('');
                  setConfirmPasswordError('');
                  setIsEmailDomainError(false);
                }}
              >
                Switch to Login
              </Button>
            </div>
          </div>
        </div>
        <AuthToaster />
      </div>
      </div>
    </>
  );
}